import { useState, KeyboardEvent, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MicrophoneIcon, StopIcon, PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { APP_CONFIG } from '@/constants';
import { speechRecognitionService } from '@/services/speechRecognitionService';
import './InputBox.css';

interface InputBoxProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  isSpeaking: boolean;
  onStopSpeaking?: () => void;
}

export const InputBox: React.FC<InputBoxProps> = ({
  onSendMessage,
  isLoading,
  isSpeaking,
  onStopSpeaking,
}) => {
  const [input, setInput] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const isSpeechSupported = speechRecognitionService.isSupported();

  const handleSend = () => {
    if (input.trim() && !isLoading) {
      onSendMessage(input);
      setInput('');
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleVoiceInput = () => {
    if (isRecording) {
      // Stop recording
      speechRecognitionService.stopListening();
      setIsRecording(false);
      setInterimTranscript('');
    } else {
      // Start recording
      setRecordingError(null);
      speechRecognitionService.startListening(
        (transcript, isFinal) => {
          if (isFinal) {
            setInput((prev) => (prev + ' ' + transcript).trim());
            setInterimTranscript('');
          } else {
            setInterimTranscript(transcript);
          }
        },
        (error) => {
          setRecordingError(error);
          setIsRecording(false);
          setInterimTranscript('');
          setTimeout(() => setRecordingError(null), 3000);
        },
        () => {
          setIsRecording(true);
        },
        () => {
          setIsRecording(false);
          setInterimTranscript('');
        }
      );
    }
  };

  useEffect(() => {
    return () => {
      if (isRecording) {
        speechRecognitionService.abort();
      }
    };
  }, [isRecording]);

  const isDisabled = isLoading || input.trim().length === 0;
  const charCount = input.length;
  const isNearLimit = charCount > APP_CONFIG.MAX_MESSAGE_LENGTH * 0.8;
  const isOverLimit = charCount > APP_CONFIG.MAX_MESSAGE_LENGTH;
  const displayText = interimTranscript || input;

  return (
    <div className="input-box-container">
      {isNearLimit && (
        <div className={`char-counter ${isOverLimit ? 'over-limit' : ''}`}>
          {charCount} / {APP_CONFIG.MAX_MESSAGE_LENGTH}
        </div>
      )}

      <div className="input-box">
        {isSpeaking && onStopSpeaking && (
          <motion.button
            onClick={onStopSpeaking}
            className="stop-speaking-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            title="Stop speaking"
          >
            <StopIcon className="w-5 h-5" />
          </motion.button>
        )}

        {isSpeechSupported && !isSpeaking && (
          <motion.button
            onClick={handleVoiceInput}
            disabled={isLoading}
            className={`voice-button ${isRecording ? 'recording' : ''}`}
            whileHover={{ scale: isLoading ? 1 : 1.05 }}
            whileTap={{ scale: isLoading ? 1 : 0.95 }}
            title={isRecording ? 'Stop recording' : 'Start voice input'}
          >
            {isRecording ? (
              <StopIcon className="w-5 h-5" />
            ) : (
              <MicrophoneIcon className="w-5 h-5" />
            )}
          </motion.button>
        )}

        <input
          type="text"
          value={displayText}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={
            isRecording
              ? 'Listening...'
              : isSpeaking
                ? 'AI is speaking...'
                : isLoading
                  ? 'AI is thinking...'
                  : 'Type a message or use voice input...'
          }
          disabled={isLoading || isRecording}
          maxLength={APP_CONFIG.MAX_MESSAGE_LENGTH}
          className={`message-input ${interimTranscript ? 'interim' : ''}`}
        />

        <motion.button
          onClick={handleSend}
          disabled={isDisabled}
          className="send-button"
          whileHover={{ scale: isDisabled ? 1 : 1.05 }}
          whileTap={{ scale: isDisabled ? 1 : 0.95 }}
          title="Send message"
        >
          <PaperAirplaneIcon className="w-6 h-6" />
        </motion.button>
      </div>

      <AnimatePresence>
        {recordingError && (
          <motion.div
            className="recording-error"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            ⚠️ {recordingError}
          </motion.div>
        )}

        {isRecording && (
          <motion.div
            className="recording-indicator"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
          >
            <span className="recording-pulse"></span>
            Recording... {interimTranscript && `"${interimTranscript}"`}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
